import axios from "axios";
import { message as AntdAlert } from "antd";
import { put } from "redux-saga/effects";
import * as actions from "../actions";
import { baseURL } from "../../../../globalUtils/axiosConfig";
import { errorHandler } from "../../../../components/unauthorizeHandler";

export function* handleCreateLocation({ data }) {
  try {
    // Remove projects array if it exists in the data
    const { projects, ...dataWithoutProjects } = data;
    
    // Ensure numeric fields are properly typed
    const payload = {
      ...dataWithoutProjects,
      latitude: parseFloat(data.latitude),
      longitude: parseFloat(data.longitude),
      radius: parseFloat(data.radius),
      status: data.status || 'active' // Set default status if not provided
    };

    const response = yield axios.post(`${baseURL}/location`, payload, {
      headers: {
        'Content-Type': 'application/json'
      },
    });
    // API always returns success case if it reaches here
    yield put(actions.createLocationSuccess(response.data));
    AntdAlert.success('Location created successfully');
    // Refresh the locations list after successful creation
    yield put(actions.fetchAllLocations({
      page: 1,
      per_page: 10,
      show_by_default: true,
    }));
  } catch (error) {
    // Handle network/unexpected errors
    const errorMessage = error.response?.data?.message || error.message || 'Failed to create location';
    AntdAlert.error(errorMessage);
    yield put(actions.createLocationError());
  }
}

export function* handleUpdateLocation({ data }) {
  try {
    // Remove id, key, and projects, and ensure numeric fields are properly typed
    const { id, key, projects, ...rest } = data;
    
    // Extract project IDs from managedProjects if managedProjectIds is empty
    let projectIds = data.managedProjectIds || [];
    if (projectIds.length === 0 && data.managedProjects && Array.isArray(data.managedProjects) && data.managedProjects.length > 0) {
      projectIds = data.managedProjects.map(project => project.id);
    }
    
    const payload = {
      ...rest,
      latitude: parseFloat(data.latitude),
      longitude: parseFloat(data.longitude),
      radius: parseFloat(data.radius),
      managedProjectIds: projectIds
    };
    const response = yield axios.patch(
      `${baseURL}/location/${data.id}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json'
        },
      }
    );

    // API always returns success case if it reaches here
    yield put(actions.updateLocationSuccess(response.data));
    AntdAlert.success('Location updated successfully');
    // Refresh the locations list after successful update
    yield put(actions.fetchAllLocations({
      page: 1,
      per_page: 10,
      show_by_default: true,
    }));
  } catch (error) {
    // Handle network/unexpected errors
    const errorMessage = error.response?.data?.message || error.message || 'Failed to update location';
    AntdAlert.error(errorMessage);
    yield put(actions.updateLocationError());
  }
}

export function* handleFetchLocations() {
  try {
    const response = yield axios.get(`${baseURL}/location`, {
      params: {
        page: 1,
        per_page: 10,
        show_by_default: true,
      },
    });
    
    // Handle both success flag and direct array response
    if (Array.isArray(response.data)) {
      // If the response is directly an array of locations
      console.log('Saga: response.data', response.data);
yield put(actions.fetchAllLocationsSuccess(response.data));
    } else if (response.data && response.data.success) {
      // If it's a success response with data property
      console.log('Saga: response.data', response.data);
yield put(actions.fetchAllLocationsSuccess(response.data));
    } else {
      // Even if not successful, we need to stop the loading state
      yield put(actions.fetchAllLocationsError({}));
      errorHandler(response.data);
    }
  } catch (error) {
    // Always make sure to stop the loading state
    yield put(actions.fetchAllLocationsError({}));
    AntdAlert.error("Something went wrong!");
    console.log(error);
  }
}

export function* handleFetchAllLocations({ data }) {
  const { search = {}, ...rest } = data || {};
  
  let paramsForAPI = { ...rest };

  if (Object.keys(search).length > 0) {
    const processedSearchFilters = { ...search };

    if (processedSearchFilters.status && Array.isArray(processedSearchFilters.status)) {
      if (processedSearchFilters.status.length > 0) {
        processedSearchFilters.status = processedSearchFilters.status.join(',');
      } else {
        // If status array is empty, remove it from the filters
        delete processedSearchFilters.status;
      }
    }

    Object.assign(paramsForAPI, processedSearchFilters);
    if (Object.keys(processedSearchFilters).length > 0) {
      // Only set show_by_default to false if there are active search filters
      // This ensures that if 'status' was the only filter and is now removed,
      // we don't incorrectly set show_by_default to false.
      paramsForAPI.show_by_default = false;
    } else if (!('show_by_default' in rest)) {
      // If all search filters are cleared (e.g. status was the only one and is now empty)
      // and show_by_default was not explicitly in 'rest', revert to true.
      // This handles the case where clearing filters should show default items.
      paramsForAPI.show_by_default = true;
    }

  }

  try {
    const response = yield axios.get(`${baseURL}/location`, {
      params: paramsForAPI,
    });

    if (response.data) {
      yield put(actions.fetchAllLocationsSuccess(response.data));
    } else {
      yield put(actions.fetchAllLocationsError({}));
    }
  } catch (error) {
    yield put(actions.fetchAllLocationsError({}));
    AntdAlert.error("Failed to fetch locations");
  }
}

export function* handleDeleteLocation({ data }) {
  try {
    const response = yield axios.delete(
      `${baseURL}/location/${data.id}`
    );
    // If axios.delete is successful (doesn't throw), we assume success.
    // The API returns the deleted object in response.data.
    AntdAlert.success("Location deleted successfully"); 
    yield put(actions.deleteLocationSuccess(response.data)); // Pass the deleted location data
    yield put(actions.fetchAllLocations({
      page: 1,
      per_page: 10,
    }));
  } catch ({ response }) { // Catch block handles actual API errors
    // It's possible response or response.data might be undefined in network errors
    const errorMessage = response?.data?.message || response?.data?.error || "Failed to delete location";
    AntdAlert.error(errorMessage);
    yield put(actions.deleteLocationError(response?.data)); // Pass error data if available
    console.log("Error deleting location:", response);
  }
}

export function* handleFetchLocation({ data }) {
  try {
    const response = yield axios.get(`${baseURL}/location/${data}`);
    const { success } = response.data;

    if (success) {
      // const { permissions, ...res } = response.data.data;
      yield put(actions.fetchLocationSuccess(response.data.data));
    } else {
      yield put(actions.updateLocationError());
      errorHandler(response.data);
    }
  } catch (error) {
    console.log(error);
  }
}

export function* handleDeleteLocationAttach({ data }) {
  try {
    const response = yield axios.delete(
      `${baseURL}/attachments/${data}`
    );
    const { success, message } = response.data;
    if (success) {
      AntdAlert.success(message);
      yield put(actions.deleteLocationAttachSuccess(response.data.data));
    } else if (!success) {
      errorHandler(response.data);
      yield put(actions.deleteLocationAttachError(response.data.data));
    }
  } catch ({ response }) {
    yield put(actions.deleteLocationAttachError(response.data.data));
    AntdAlert.error("Something went wrong!");
    console.log(response);
  }
}

export function* handleUpdateLocationNotifications({ data }) {
  try {
    const fd = new FormData();
    for (const [key, value] of Object.entries(data)) {
      if (value !== null && value !== undefined) {
        fd.append(key, value);
      }
    }
    const response = yield axios.put(
      `${baseURL}/location/${data.id}`,
      fd,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    );
    const { success, message } = response.data;
    if (success) {
      AntdAlert.success(message);
      yield put(actions.updateLocationNotificationsSuccess(response.data.data));
    } else if (!success) {
      AntdAlert.error(response.data?.errors[0]);
      yield put(actions.updateLocationNotificationsError());
      errorHandler(response.data);
    }
  } catch (error) {
    console.log(error);
  }
}
